import { useState } from "react";
import { Button, notification, Tooltip } from "antd";
import { FileExcelOutlined, LoadingOutlined } from "@ant-design/icons";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import config from '../../config';


// ─── Paleta de colores ────────────────────────────────────────────────────────
const COLOR = {
  FACTOR_BG: "FFECF5E7",
  HEADER_BG: "FF4472C4",
  HEADER_FG: "FFFFFFFF",
  ANIO_BG:   "FF1F3864",
  ANIO_FG:   "FFFFFFFF",
  MES_BG:    "FF2E75B6", // columna mes programado
  MES_FG:    "FFFFFFFF",
  STRIPE:    "FFDEEAF1",
  WHITE:     "FFFFFFFF",
  BORDER:    "FFB8CCE4",
};

// ─── Helpers de estilo ────────────────────────────────────────────────────────
const mkFont   = (bold = false, color = "FF000000", size = 9) => ({ name: "Arial", bold, color: { argb: color }, size });
const mkFill   = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });
const mkBorder = () => { const s = { style: "thin", color: { argb: COLOR.BORDER } }; return { top: s, bottom: s, left: s, right: s }; };
const center   = (wrap = false) => ({ horizontal: "center", vertical: "middle", wrapText: wrap });
const left     = (wrap = true)  => ({ horizontal: "left",   vertical: "middle", wrapText: wrap });

// ─── Índices de columnas fijas ────────────────────────────────────────────────
//  A        B               C        D        E       F            G      H      I       J      K       L
// FACTOR | COD_ACT | ACCION | COD_TAR | TAREA | INDICADOR | TIPO | ACTOR | MEDIO | N_ACT | N_EJEC | PCT_ACT | PCT_COMP
const COL = {
  FACTOR:       1,
  COD_ACTIVIDAD:2,
  ACCION:       3,
  COD_TAREA:    4,
  TAREA:        5,
  INDICADOR:    6,
  TIPO:         7,
  ACTOR:        8,
  MEDIO:        9,
  N_ACT:        10,
  N_EJEC:       11,
  PCT_ACT:      12,
  PCT_COMP:     13,
};
const N_FIJAS = 13;
const COL_WIDTHS = {
  1:22,  // Factor
  2:8,   // Cód. Actividad
  3:40,  // Acción de fortalecimiento
  4:8,   // Cód. Tarea
  5:45,  // Tarea
  6:32,  // Indicador
  7:12,  // Tipo actividad
  8:10,  // Actor
  9:14,  // Medio verificación
  10:9,  // No. actividades
  11:9,  // No. ejecutados
  12:10, // % avance actividad
  13:10, // % avance componente
};

// ─── Normaliza clave de mes: "MAR" → "MAR", ya viene en mayúsculas ────────────
// La clave compuesta es "2025|MAR"
const cronoKey = (ano, mes_poa) => `${String(ano).trim()}|${String(mes_poa).trim().toUpperCase()}`;

// ─── Construye estructura dinámica de cronograma ─────────────────────────────
// Retorna [{ anio:"2025", meses:["MAR","ABR",…] }, { anio:"2026", meses:[…] }]
// ordenado por año y por posición del mes dentro del POA (usa el campo "mes" = "MES 1", "MES 2"…)
function buildCronoEstructura(filas) {
  // map: { "2025": Map<mes_poa, orden> }
  // Usamos el número de "MES N" como orden para respetar la secuencia real del POA
  const mapAnio = {}; // { anio: { mes_poa: ordenNumerico } }

  for (const f of filas) {
    for (const c of f.cronogramasP ?? []) {
      const anio    = String(c.ano).trim();
      const mesPoa  = String(c.mes_poa).trim().toUpperCase();
      // "MES 4" → 4
      const orden   = parseInt(String(c.mes).replace(/[^0-9]/g, ""), 10) || 9999;

      if (!mapAnio[anio]) mapAnio[anio] = {};
      // guardar el orden menor (por si hay duplicados)
      if (mapAnio[anio][mesPoa] === undefined || orden < mapAnio[anio][mesPoa]) {
        mapAnio[anio][mesPoa] = orden;
      }
    }
  }

  return Object.keys(mapAnio)
    .sort() // "2025" < "2026"
    .map((anio) => {
      const mesesOrdenados = Object.entries(mapAnio[anio])
        .sort((a, b) => a[1] - b[1]) // por orden numérico del POA
        .map(([mesPoa]) => mesPoa);
      return { anio, meses: mesesOrdenados };
    });
}

// ─── Agrupa cronograma en mapa para lookup O(1) ───────────────────────────────
// Retorna { "2025|MAR": "100.00", "2025|ABR": "0.00", … }
function buildCronoMapa(cronogramasP) {
  const m = {};
  for (const c of cronogramasP ?? []) {
    const k   = cronoKey(c.ano, c.mes_poa);
    m[k] = c.avance ?? "";
  }
  return m;
}

// ─── Flatten árbol → filas planas ─────────────────────────────────────────────
function flattenLocal(data) {
  const filas = [];
  for (const actividad of data) {
    const baseA = {
      actividad_id:           actividad.id,
      codigo_actividad:       actividad.codigo_actividad ?? "",
      accion_fortalecimiento: actividad.accion_fortalecimiento
        ?? `${actividad.codigo_actividad ?? ""} ${actividad.descripcion ?? ""}`.trim(),
      nombre_fase: actividad.nombre_fase,
    };
    for (const tarea of actividad.children ?? []) {
      const baseT = {
        tarea_id:          tarea.id,
        codigo_tarea:      tarea.codigo_tarea ?? "",
        descripcion_tarea: tarea.descripcion,
      };
      for (const ind of tarea.children ?? []) {
        const medio = ind.medio_verificacion;
        filas.push({
          ...baseA,
          ...baseT,
          indicador_id:       ind.id,
          codigo_indicador:   ind.codigo_indicador ?? "",
          indicador:          ind.indicador ?? "",
          meta:               ind.meta ?? "",
          meta_ejecutada:     ind.meta_ejecutada ?? "",
          tipo_actividad:     ind.tipo_actividad ?? "",
          actor:              ind.actor ?? "",
          medio_verificacion: medio?.medio_verificacion ?? "",
          // Solo cronogramasP — ordenados por el número de "MES N"
          cronogramasP: (ind.cronogramasP ?? []).sort((a, b) => {
            const na = parseInt(String(a.mes).replace(/[^0-9]/g, ""), 10) || 0;
            const nb = parseInt(String(b.mes).replace(/[^0-9]/g, ""), 10) || 0;
            return na - nb;
          }),
        });
      }
    }
  }
  return filas;
}

// ─── Función principal de generación del Excel ───────────────────────────────
async function generarExcel(filas, detalleProyecto) {
  const wb = new ExcelJS.Workbook();
  wb.creator = "SistemaPA";
  wb.created = new Date();

  const ws = wb.addWorksheet("Plan Operativo", {
    views: [{ state: "frozen", xSplit: N_FIJAS, ySplit: 3 }],
    pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1 },
  });

  // ── Estructura dinámica (1 columna por mes, no P+E) ──────────────────────
  const estructura   = buildCronoEstructura(filas);
  // totalMesCols: 1 col por mes (ya no hay E)
  const totalMesCols = estructura.reduce((s, e) => s + e.meses.length, 0);
  const TOTAL_COLS   = N_FIJAS + totalMesCols;

  // Anchos columnas fijas
  for (const [col, w] of Object.entries(COL_WIDTHS)) ws.getColumn(Number(col)).width = w;

  // Anchos columnas de mes (8 cada una)
  for (let i = N_FIJAS + 1; i <= TOTAL_COLS; i++) ws.getColumn(i).width = 8;

  // ════════════════════════════════════════════════════════════
  // FILA 1 — Título + cabeceras de AÑO
  // ════════════════════════════════════════════════════════════
  ws.getRow(1).height = 28;
  ws.mergeCells(1, 1, 1, N_FIJAS);
  const tit     = ws.getCell(1, 1);
  tit.value = `PLAN OPERATIVO IMPLEMENTACIÓN COMPONENTE FORMACIÓN  Y ACOMPAÑAMIENTO ESTRATEGIA DE SOSTENIBILIDAD  - ${detalleProyecto.codigo_interno}- ${detalleProyecto.implementador}`;
  tit.font      = mkFont(true, COLOR.HEADER_FG, 13);
  tit.fill      = mkFill(COLOR.ANIO_BG);
  tit.alignment = center();
  tit.border    = mkBorder();

  let ci = N_FIJAS + 1;
  for (const { anio, meses } of estructura) {
    const span = meses.length; // 1 col por mes
    if (span > 1) ws.mergeCells(1, ci, 1, ci + span - 1);
    const c     = ws.getCell(1, ci);
    c.value     = String(anio);
    c.font      = mkFont(true, COLOR.ANIO_FG, 11);
    c.fill      = mkFill(COLOR.ANIO_BG);
    c.alignment = center();
    c.border    = mkBorder();
    ci         += span;
  }

  // ════════════════════════════════════════════════════════════
  // FILA 2 — Cabeceras columnas fijas + "MES N\nMes" (una col)
  // ════════════════════════════════════════════════════════════
  ws.getRow(2).height = 42;
  const HEADS = [
    "FACTOR",
    "Cód.\nActividad",
    "ACCIÓN DE FORTALECIMIENTO",
    "Cód.\nTarea",
    "ACTIVIDADES DERIVADAS",
    "Subactividad",
    "Tipo de actividad",
    "Actor",
    "Medios de verificación",
    "No. actividades",
    "No. ejecutados",
    "% avance por actividad",
    "% avance por componente",
  ];
  HEADS.forEach((h, i) => {
    const c     = ws.getCell(2, i + 1);
    c.value     = h;
    c.font      = mkFont(true, COLOR.HEADER_FG, 9);
    c.fill      = mkFill(COLOR.HEADER_BG);
    c.alignment = center(true);
    c.border    = mkBorder();
  });

  // Para la cabecera de mes usamos "MES 1\nMAR" etc.
  // Necesitamos el número de orden; lo sacamos de la primera fila que tenga ese mes
  const ordenMes = {}; // { "2025|MAR": "MES 1" }
  for (const f of filas) {
    for (const c of f.cronogramasP ?? []) {
      const k = cronoKey(c.ano, c.mes_poa);
      if (!ordenMes[k]) ordenMes[k] = String(c.mes).trim(); // "MES 1"
    }
  }

  ci = N_FIJAS + 1;
  for (const { anio, meses } of estructura) {
    for (const mesPoa of meses) {
      const k        = cronoKey(anio, mesPoa);
      const mesLabel = ordenMes[k] ?? mesPoa;
      const c        = ws.getCell(2, ci);
      c.value        = `${mesLabel}\n${mesPoa}`;
      c.font         = mkFont(true, COLOR.MES_FG, 8);
      c.fill         = mkFill(COLOR.MES_BG);
      c.alignment    = center(true);
      c.border       = mkBorder();
      ci++;
    }
  }

  // ════════════════════════════════════════════════════════════
  // FILA 3 — Sub-cabecera "P" (solo programado)
  // ════════════════════════════════════════════════════════════
  ws.getRow(3).height = 16;
  for (let i = 1; i <= N_FIJAS; i++) {
    const c = ws.getCell(3, i);
    c.fill  = mkFill(COLOR.HEADER_BG);
    c.border = mkBorder();
  }
  for (let i = N_FIJAS + 1; i <= TOTAL_COLS; i++) {
    const c     = ws.getCell(3, i);
    c.value     = "P";
    c.font      = mkFont(true, COLOR.MES_FG, 8);
    c.fill      = mkFill(COLOR.MES_BG);
    c.alignment = center();
    c.border    = mkBorder();
  }

  // ════════════════════════════════════════════════════════════
  // FILAS DE DATOS — merge vertical Factor / Actividad / Tarea
  // ════════════════════════════════════════════════════════════

  // Agrupar para calcular spans
  const grupos = [];
  let lastF = null, lastA = null, lastT = null;
  let gF = null, gA = null, gT = null;

  for (const f of filas) {
    const fKey = f.nombre_fase;
    const aKey = String(f.actividad_id);
    const tKey = String(f.tarea_id);

    if (fKey !== lastF) {
      gF = { label: f.nombre_fase, actividades: [] };
      grupos.push(gF);
      lastF = fKey; lastA = null; lastT = null;
    }
    if (aKey !== lastA) {
      gA = { label: f.accion_fortalecimiento, tareas: [] };
      gF.actividades.push(gA);
      lastA = aKey; lastT = null;
    }
    if (tKey !== lastT) {
      gT = { label: f.descripcion_tarea, indicadores: [] };
      gA.tareas.push(gT);
      lastT = tKey;
    }
    gT.indicadores.push(f);
  }

  let excelRow = 4;

  const writeCell = (row, col, value, fillArgb, alignFn, bold = false) => {
    const c     = ws.getCell(row, col);
    c.value     = value;
    c.font      = mkFont(bold, "FF000000", 9);
    c.fill      = mkFill(fillArgb);
    c.alignment = alignFn;
    c.border    = mkBorder();
  };

  for (const factor of grupos) {
    const factorStart = excelRow;
    const factorSpan  = factor.actividades.reduce(
      (s, a) => s + a.tareas.reduce((ss, t) => ss + t.indicadores.length, 0), 0
    );

    for (const activ of factor.actividades) {
      const activStart = excelRow;
      const activSpan  = activ.tareas.reduce((s, t) => s + t.indicadores.length, 0);

      for (const tarea of activ.tareas) {
        const tareaStart = excelRow;
        const tareaSpan  = tarea.indicadores.length;

        for (let i = 0; i < tareaSpan; i++) {
          const f      = tarea.indicadores[i];
          const row    = excelRow;
          const fillBg = row % 2 === 0 ? COLOR.STRIPE : COLOR.WHITE;

          ws.getRow(row).height = 35;

          // ── Factor (A) ──
          if (i === 0 && tarea === activ.tareas[0] && activ === factor.actividades[0]) {
            writeCell(row, COL.FACTOR, factor.label, COLOR.FACTOR_BG, center(true), true);
          } else {
            const c = ws.getCell(row, COL.FACTOR);
            c.fill = mkFill(COLOR.FACTOR_BG); c.border = mkBorder();
          }

          // ── Cód. Actividad (B) ──
          if (i === 0 && tarea === activ.tareas[0]) {
            writeCell(row, COL.COD_ACTIVIDAD, f.codigo_actividad, fillBg, center(), true);
          } else {
            const c = ws.getCell(row, COL.COD_ACTIVIDAD);
            c.fill = mkFill(fillBg); c.border = mkBorder();
          }

          // ── Actividad (C) ──
          if (i === 0 && tarea === activ.tareas[0]) {
            writeCell(row, COL.ACCION, activ.label, fillBg, left());
          } else {
            const c = ws.getCell(row, COL.ACCION);
            c.fill = mkFill(fillBg); c.border = mkBorder();
          }

          // ── Cód. Tarea (D) ──
          if (i === 0) {
            writeCell(row, COL.COD_TAREA, f.codigo_tarea, fillBg, center(), true);
          } else {
            const c = ws.getCell(row, COL.COD_TAREA);
            c.fill = mkFill(fillBg); c.border = mkBorder();
          }

          // ── Tarea (E) ──
          if (i === 0) {
            writeCell(row, COL.TAREA, tarea.label, fillBg, left());
          } else {
            const c = ws.getCell(row, COL.TAREA);
            c.fill = mkFill(fillBg); c.border = mkBorder();
          }

          // ── Indicador y campos ──
          writeCell(row, COL.INDICADOR, f.indicador,          fillBg, left());
          writeCell(row, COL.TIPO,      f.tipo_actividad,     fillBg, center());
          writeCell(row, COL.ACTOR,     f.actor,              fillBg, center());
          writeCell(row, COL.MEDIO,     f.medio_verificacion, fillBg, left());
          writeCell(row, COL.N_ACT,     f.meta,               fillBg, center());
          writeCell(row, COL.N_EJEC,    f.meta_ejecutada,     fillBg, center());

          // ── % avance actividad = SUMA de avances de los meses / N_ACT ──
          // Las columnas de meses de esta fila van de (N_FIJAS+1) a TOTAL_COLS
          const aNAct   = ws.getCell(row, COL.N_ACT).address;
          const cMesIni = ws.getCell(row, N_FIJAS + 1).address;
          const cMesFin = ws.getCell(row, TOTAL_COLS).address;
          const cPA     = ws.getCell(row, COL.PCT_ACT);
          cPA.value     = { formula: `=IF(${aNAct}=0,0,SUM(${cMesIni}:${cMesFin})/${aNAct})` };
          cPA.numFmt    = "0.00%";
          cPA.font      = mkFont(false, "FF000000", 9);
          cPA.fill      = mkFill(fillBg);
          cPA.alignment = center();
          cPA.border    = mkBorder();

          // ── % avance por componente — solo primera fila del FACTOR ──
          const cPC     = ws.getCell(row, COL.PCT_COMP);
          cPC.font      = mkFont(false, "FF000000", 9);
          cPC.fill      = mkFill(fillBg);
          cPC.alignment = center();
          cPC.border    = mkBorder();
          if (i === 0 && tarea === activ.tareas[0] && activ === factor.actividades[0]) {
            const pct1 = ws.getCell(factorStart, COL.PCT_ACT).address;
            const pct2 = ws.getCell(factorStart + factorSpan - 1, COL.PCT_ACT).address;
            cPC.value  = { formula: `=IFERROR(AVERAGE(${pct1}:${pct2}),0)` };
            cPC.numFmt = "0.00%";
          }

          // ── Cronograma — 1 columna por mes ──
          // Normalización: si avance <= 1 queda tal cual; si > 1 → dividir / 100
          const cronoMapa = buildCronoMapa(f.cronogramasP);
          ci = N_FIJAS + 1;

          for (const { anio, meses } of estructura) {
            for (const mesPoa of meses) {
              const k   = cronoKey(anio, mesPoa);
              const val = cronoMapa[k]; // undefined si no tiene ese mes

              const cM     = ws.getCell(row, ci);
              const tieneVal = val !== undefined && val !== "";
              cM.value     = tieneVal ? Number(val) : "";
              cM.numFmt    = tieneVal ? "0.00" : "General";
              cM.font      = tieneVal ? mkFont(true, COLOR.MES_FG, 9) : mkFont(false, "FF000000", 9);
              cM.fill      = tieneVal ? mkFill(COLOR.MES_BG) : mkFill(fillBg);
              cM.alignment = center();
              cM.border    = mkBorder();
              ci++;
            }
          }

          excelRow++;
        } // fin indicadores

        // Merges verticales de tarea
        if (tareaSpan > 1) {
          ws.mergeCells(tareaStart, COL.COD_TAREA, tareaStart + tareaSpan - 1, COL.COD_TAREA);
          ws.mergeCells(tareaStart, COL.TAREA,     tareaStart + tareaSpan - 1, COL.TAREA);
        }
      } // fin tareas

      if (activSpan > 1) {
        ws.mergeCells(activStart, COL.COD_ACTIVIDAD, activStart + activSpan - 1, COL.COD_ACTIVIDAD);
        ws.mergeCells(activStart, COL.ACCION,        activStart + activSpan - 1, COL.ACCION);
      }
    } // fin actividades

    // PCT_COMP merge al nivel de Factor (igual que columna FACTOR)
    if (factorSpan > 1) {
      ws.mergeCells(factorStart, COL.FACTOR,   factorStart + factorSpan - 1, COL.FACTOR);
      ws.mergeCells(factorStart, COL.PCT_COMP, factorStart + factorSpan - 1, COL.PCT_COMP);
    }
  } // fin factores

  // ── Fila de totales ────────────────────────────────────────────────────────
  const totalRow = excelRow;
  ws.getRow(totalRow).height = 20;
  ws.mergeCells(totalRow, 1, totalRow, N_FIJAS - 4);
  const totLbl     = ws.getCell(totalRow, 1);
  totLbl.value     = "TOTALES";
  totLbl.font      = mkFont(true, COLOR.HEADER_FG, 9);
  totLbl.fill      = mkFill(COLOR.HEADER_BG);
  totLbl.alignment = center();
  totLbl.border    = mkBorder();

  for (const colNum of [COL.N_ACT, COL.N_EJEC]) {
    const a1    = ws.getCell(4, colNum).address;
    const a2    = ws.getCell(excelRow - 1, colNum).address;
    const cTot  = ws.getCell(totalRow, colNum);
    cTot.value  = { formula: `=SUM(${a1}:${a2})` };
    cTot.font   = mkFont(true, COLOR.HEADER_FG, 9);
    cTot.fill   = mkFill(COLOR.HEADER_BG);
    cTot.alignment = center();
    cTot.border = mkBorder();
  }

  ws.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: TOTAL_COLS } };

  const buffer = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `PlanOperativo_${new Date().toISOString().slice(0, 10)}_${detalleProyecto.codigo_interno}.xlsx`
  );
}

// ─── Componente ───────────────────────────────────────────────────────────────
export default function ExportarPOExcel({ oid_proyecto }) {
  const [loading, setLoading] = useState(false);

  const handleExportar = async () => {
    if (!oid_proyecto) {
      notification.warning({ message: "Sin proyecto", description: "No se recibió un oid_proyecto válido." });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${config.baseUrl}/api/poaPOCompleto?oid_proyecto=${oid_proyecto}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();

      if (!json.success || !json.data?.length) {
        notification.info({ message: "Sin datos", description: "No hay registros para exportar." });
        return;
      }

      const filas = flattenLocal(json.data);

      if (!filas.length) {
        notification.warning({ message: "Sin indicadores", description: "El proyecto no tiene indicadores registrados." });
        return;
      }

      await generarExcel(filas, json.detalleProyecto);
      notification.success({ message: "¡Excel generado!", description: "El archivo se descargó correctamente." });
    } catch (err) {
      console.error(err);
      notification.error({ message: "Error al exportar", description: err?.message ?? "Error desconocido." });
    } finally {
      setLoading(false);
    }
  };

  return (
<Tooltip title="Descargar POA">
  <Button
    type="primary"
    icon={loading ? <LoadingOutlined /> : <FileExcelOutlined />}
    loading={loading}
    onClick={handleExportar}
    style={{
      backgroundColor: "#1F3864",
      borderColor: "#1F3864",
      fontWeight: 600,
    }}
  />
</Tooltip>
  );
}